// ============================================================================
// DAS NOTIFICATIONS HOOK
// ============================================================================
// React hook for managing real-time notifications with polling

import { useState, useEffect, useCallback, useRef } from 'react';
import { dasNotificationService } from '../../api/services/dasNotificationService';
import type { DASNotification, ID } from '../../api/types';

/**
 * Polling interval in milliseconds
 */
const POLLING_INTERVAL = 30000; // 30 seconds

/**
 * useDASNotifications Hook
 * Manages notifications with automatic polling for real-time updates
 */
export const useDASNotifications = (enablePolling = true) => {
  const [notifications, setNotifications] = useState<DASNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true for initial fetch
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const initialFetchDone = useRef(false);

  /**
   * Fetch all notifications
   */
  const fetchNotifications = useCallback(async (unreadOnly = false) => {
    if (!initialFetchDone.current) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await dasNotificationService.getAll(unreadOnly);

      if (isMountedRef.current) {
        let items: DASNotification[] = [];

        // Handle multiple response formats
        if (Array.isArray(response)) {
          items = response;
        } else if (response && typeof response === 'object') {
          if (Array.isArray((response as any).items)) {
            items = (response as any).items;
          } else if ((response as any).data) {
            const data = (response as any).data;
            if (Array.isArray(data)) {
              items = data;
            } else if (data && Array.isArray(data.items)) {
              items = data.items;
            }
          }
        }

        setNotifications(items);
        initialFetchDone.current = true;
      }
    } catch (err: any) {
      if (isMountedRef.current) {
        const errorMessage = err?.response?.data?.message || 'Failed to fetch notifications';
        setError(errorMessage);
        initialFetchDone.current = true;
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * Fetch unread count
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await dasNotificationService.getUnreadCount();

      if (isMountedRef.current) {
        // Extract unreadCount from response object
        const count = typeof response.data === 'object' && response.data !== null
          ? (response.data as { unreadCount: number }).unreadCount
          : (response.data as number) || 0;
        setUnreadCount(count);
      }
    } catch (err: any) {
      // Silently fail - count will remain at previous value
    }
  }, []);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId: ID) => {
    try {
      await dasNotificationService.markAsRead(notificationId);

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to mark notification as read';
      setError(errorMessage);
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await dasNotificationService.markAllAsRead();

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to mark all as read';
      setError(errorMessage);
    }
  }, []);

  /**
   * Delete notification
   */
  const deleteNotification = useCallback(async (notificationId: ID) => {
    try {
      await dasNotificationService.delete(notificationId);

      // Update local state
      setNotifications((prev) => {
        const notification = prev.find((n) => n.id === notificationId);
        const filtered = prev.filter((n) => n.id !== notificationId);

        // Decrement unread count if the deleted notification was unread
        if (notification && !notification.isRead) {
          setUnreadCount((count) => Math.max(0, count - 1));
        }

        return filtered;
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to delete notification';
      setError(errorMessage);
    }
  }, []);

  /**
   * Refresh notifications and unread count
   */
  const refresh = useCallback(async () => {
    await Promise.all([fetchNotifications(), fetchUnreadCount()]);
  }, [fetchNotifications, fetchUnreadCount]);

  /**
   * Start polling
   */
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;

    pollingIntervalRef.current = setInterval(() => {
      fetchUnreadCount();
    }, POLLING_INTERVAL);
  }, [fetchUnreadCount]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * Initial load and polling setup
   */
  useEffect(() => {
    isMountedRef.current = true;
    initialFetchDone.current = false;

    // Initial fetch - run both in parallel
    const initialLoad = async () => {
      await Promise.all([fetchNotifications(), fetchUnreadCount()]);
    };
    initialLoad();

    // Start polling if enabled
    if (enablePolling) {
      startPolling();
    }

    // Cleanup
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [fetchNotifications, fetchUnreadCount, enablePolling, startPolling, stopPolling]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
    startPolling,
    stopPolling,
  };
};

export default useDASNotifications;
